import React from "react";
import { Alert, IconButton, Box } from "@mui/joy";
import {
  InfoIcon,
  WarningIcon,
  ReportIcon,
  CheckCircleIcon,
  CloseRoundedIcon,
} from "../../styles/StyledMUI";
import type { ColorPaletteProp } from "@mui/joy/styles";

interface ToastProviderProps {
  children: React.ReactNode;
}

export interface AlertContextValue {
  showAlert: (newMessage: string, newSeverity: ColorPaletteProp) => void;
}

export type ShowAlertFn = AlertContextValue["showAlert"];

export const AlertContext = React.createContext<AlertContextValue>(null!);

export function useAlert(): AlertContextValue {
  const context = React.useContext(AlertContext);
  if (!context) {
    throw new Error("useAlert must be used within a ToastProvider (AlertProvider)");
  }
  return context;
}

export function AlertProvider({ children }: ToastProviderProps) {
  const [open, setOpen] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [severity, setSeverity] = React.useState<ColorPaletteProp>("neutral");

  const showAlert = React.useCallback((newMessage: string, newSeverity: ColorPaletteProp) => {
    setMessage(newMessage);
    setSeverity(newSeverity);
    setOpen(true);
    setTimeout(() => setOpen(false), 3000);
  }, []);

  const hideAlert = React.useCallback(() => setOpen(false), []);

  const value = React.useMemo<AlertContextValue>(() => ({ showAlert }), [showAlert]);

  const icons = {
    primary: <></>,
    success: <CheckCircleIcon />,
    warning: <WarningIcon />,
    danger: <ReportIcon />,
    neutral: <InfoIcon />,
  };

  return (
    <AlertContext.Provider value={value}>
      {children}
      {open && (
        <Box
          sx={{
            position: "fixed",
            bottom: { xs: 12, sm: 20 },
            left: "50%",
            transform: open ? "translate(-50%, 0)" : "translate(-50%, 20px)",
            // Above modals (10000), confirmation, Joy popups, DarkSelect, etc.
            zIndex: 10100,
            width: { xs: "92vw", sm: 420 },
            maxWidth: 560,
            opacity: open ? 1 : 0,
            transition: "opacity 0.25s ease, transform 0.25s ease",
          }}
        >
          <Alert
            color={severity}
            variant="solid"
            size="sm"
            startDecorator={icons[severity]}
            endDecorator={
              <IconButton
                variant="plain"
                size="sm"
                color="neutral"
                onClick={hideAlert}
                aria-label="Fermer la notification"
              >
                <CloseRoundedIcon />
              </IconButton>
            }
            sx={{
              borderRadius: "8px",
              boxShadow: "lg",
              px: { xs: 1.5, sm: 2 },
              py: { xs: 1, sm: 1.25 },
              display: "flex",
              alignItems: "center",
              fontSize: { xs: "0.85rem", sm: "0.95rem" },
            }}
          >
            {message}
          </Alert>
        </Box>
      )}
    </AlertContext.Provider>
  );
}
